import styled from "styled-components";

import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
} from "../../../../utils/constants";
import { AmountVariant } from "./types";
import Button, { BaseButton } from "../../../../components/Button";

const variantColor = (variant: AmountVariant): string => {
  if (variant === "income") {
    return "var(--color-success)";
  }
  if (variant === "expense") {
    return "var(--color-error)";
  }
  return "var(--color-text-default)";
};

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  border-radius: ${SPACING.md}px;
  background: var(--color-background-2);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-small);

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px;
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.md}px;
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;

  @media ${QUERIES.tabletLargeAndUp} {
    flex-direction: row;
    align-items: baseline;
    gap: ${SPACING.md}px;
  }
`;

export const Title = styled.h2`
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

export const Summary = styled.p`
  margin: 0;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  font-style: italic;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-secondary);
  line-height: 1;
`;

export const SummaryValue = styled.span<{ $variant: AmountVariant }>`
  font-weight: 700;
  font-style: normal;
  color: ${({ $variant }) => variantColor($variant)};
`;

export const AddButton = styled(Button)`
  flex-shrink: 0;
`;

export const Card = styled.article`
  flex: 0 0 240px;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px;
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
  background: var(--color-background-0);
  min-width: 0;
`;

export const SkeletonRow = styled.div`
  display: flex;
  gap: ${SPACING.md}px;
  padding-bottom: ${SPACING.xs}px;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const DateBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 8px;
  border: 1px solid var(--color-border-light);
  background: var(--color-background-2);
`;

export const DateDay = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-lg);
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-default);
`;

export const DateDivider = styled.div`
  background: var(--color-card-border);
  flex-shrink: 0;
  height: 1px;
  width: 100%;
`;

export const DateMonth = styled.span`
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-text-muted);
`;

export const CardHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs}px;
`;

export const Frequency = styled.span`
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

export const MenuWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

export const Menu = styled.div<{ $top: number; $right: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  right: ${({ $right }) => $right}px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  min-width: 120px;
  padding: ${SPACING.xs}px;
  border-radius: 10px;
  border: 1px solid var(--color-border-light);
  background: var(--color-card-background);
  box-shadow: var(--shadow-medium);
`;

export const MenuItem = styled(BaseButton)<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  width: 100%;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border: none;
  border-radius: 6px;
  background: none;
  text-align: left;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  font-weight: 600;
  cursor: pointer;
  color: ${({ $danger }) =>
    $danger ? "var(--color-error)" : "var(--color-text-default)"};
  transition: background 0.15s ease;

  &:hover {
    background: ${({ $danger }) =>
      $danger ? "var(--color-error-background)" : "var(--hover-background)"};
  }

  & > svg {
    font-size: var(--text-md);
  }
`;

export const CardTitle = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--color-text-default);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const Amount = styled.span<{ $variant: AmountVariant }>`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 700;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${({ $variant }) => variantColor($variant)};
`;

export const CountdownPill = styled.span<{ $isSoon: boolean }>`
  flex-shrink: 0;
  padding: ${SPACING.xxs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.1em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  border: 1px solid
    ${({ $isSoon }) =>
      $isSoon ? "var(--color-warning-border)" : "var(--color-border)"};
  background: ${({ $isSoon }) =>
    $isSoon ? "var(--color-warning-background)" : "transparent"};
  color: ${({ $isSoon }) =>
    $isSoon ? "var(--color-warning)" : "var(--color-text-muted)"};
`;

export const EmptyCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.lg}px;
  padding: ${SPACING.xl}px ${SPACING.lg}px;
  border: 1px dashed var(--empty-state-border);
  border-radius: ${SPACING.md}px;
  background: var(--empty-state-background);
  text-align: center;

  @media ${QUERIES.tabletLargeAndUp} {
    flex-direction: row;
    gap: ${SPACING.md}px;
    text-align: left;
  }
`;

export const EmptyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.sm}px;

  @media ${QUERIES.tabletLargeAndUp} {
    flex-direction: row;
    gap: ${SPACING.md}px;
  }
`;

export const EmptyIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--empty-state-icon-color);

  & > svg {
    font-size: var(--text-display);
  }
`;

export const EmptyText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const EmptyTitle = styled.h2`
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

export const EmptyDescription = styled.p`
  margin: 0;
  font-size: var(--text-base);
  color: var(--color-text-muted);
`;

export const SkeletonCard = styled.div`
  flex: 0 0 240px;
  height: 120px;
  border-radius: 12px;
  background: linear-gradient(
    90deg,
    var(--hover-background) 0%,
    var(--color-card-background) 50%,
    var(--hover-background) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;
